import accountApi from './api/AccountApiClient';
import {
  normalizeDeliveryAddress,
  validateDeliveryAddress,
  sameDeliveryDestination,
  deliveryDetails,
} from './deliveryAddress';

const pendingSaves = new Map();

export default function resolveDeliveryAddress(
  value,
  userId,
  isCurrent = () => true,
  { primaryForFirstAddress = false, saveDetails = false } = {}
) {
  const selected = normalizeDeliveryAddress(value);
  if (!userId) {
    try {
      return Promise.resolve(validateDeliveryAddress(selected));
    } catch (error) {
      error.deliveryValidation = true;
      return Promise.reject(error);
    }
  }
  // Shared by checkout and the modal, not by a component instance. No retries.
  const key = JSON.stringify([
    String(userId),
    primaryForFirstAddress,
    saveDetails,
    ...['address', 'city', 'state', 'zipcode', 'latitude', 'longitude'].map(
      (field) => selected?.[field]
    ),
    deliveryDetails(selected?.addition),
  ]);
  let pending = pendingSaves.get(key);
  if (!pending) {
    pending = { callers: new Set() };
    pendingSaves.set(key, pending);
    pending.operation = (async () => {
      // Cached lists and IDs may belong to a previous account.
      const response = await accountApi.address.getAddresses();
      // A cancelled modal must not cancel checkout (or vice versa). Only skip
      // an unstarted write when nobody still needs this account/destination.
      if (![...pending.callers].some((current) => current()))
        throw new Error('Order context changed');
      if (!Array.isArray(response)) throw new Error('Address list unavailable');
      const saved = response
        .filter((item) => item.active !== false)
        .map(normalizeDeliveryAddress);
      const exactMatch =
        selected &&
        saved.find((item) => sameDeliveryDestination(selected, item));
      // An instruction edit is not a new street address. Reuse only an owned,
      // independently fetched saved destination; never trust the incoming ID.
      const ownedMatch =
        saveDetails &&
        selected &&
        saved.find(
          (item) =>
            item.address_id === selected.address_id &&
            sameDeliveryDestination(
              { ...selected, addition: {} },
              { ...item, addition: {} }
            )
        );
      const match = ownedMatch || exactMatch;
      let complete;
      try {
        complete = validateDeliveryAddress(
          match
            ? {
                ...selected,
                ...match,
                addition: selected.addition ?? match.addition,
              }
            : selected
        );
      } catch (error) {
        error.deliveryValidation = true;
        throw error;
      }
      if (match?.address_id) {
        if (
          saveDetails &&
          JSON.stringify(deliveryDetails(match.addition)) !==
            JSON.stringify(deliveryDetails(selected.addition))
        ) {
          await accountApi.address.updateAddress(
            match.address_id,
            deliveryDetails(selected.addition)
          );
        }
        return { ...complete, user_id: userId, address_id: match.address_id };
      }
      // Registration deliberately makes the first address primary. Reconciliation
      // for an existing account must not replace its address preferences.
      const primary = primaryForFirstAddress && saved.length === 0;
      const created = await accountApi.address.createAddress({
        address: complete.address,
        city: complete.city,
        state: complete.state,
        zip: complete.zipcode,
        latitude: complete.latitude,
        longitude: complete.longitude,
        ...(complete.addition ? { addition: complete.addition } : {}),
        primary,
      });
      const id = created?.address?.id ?? created?.id;
      if (!id) throw new Error('Address save did not return an ID');
      return {
        ...complete,
        id,
        address_id: id,
        user_id: userId,
        primary,
        is_primary: primary,
      };
    })();
    pending.operation.then(
      () => pendingSaves.delete(key),
      () => pendingSaves.delete(key)
    );
  }
  pending.callers.add(isCurrent);
  // The account operation is shared, but each caller owns its result. A write
  // already in flight is never retried or undone on behalf of a stale caller.
  return pending.operation.finally(() => {
    if (!isCurrent()) throw new Error('Order context changed');
  });
}
